import { useEffect, useMemo, useState } from 'react';

import { SecondaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { VENUES_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import {
  RelatedLinkView,
  SeoContentView,
  V4StorefrontSearchRequest,
} from '@zola/svc-marketplace-ts-types';
import { Masthead, SupportAccordion } from '@zola/zola-ui/src/components/PreAuthV3';
import {
  BackgroundType,
  FeaturedImageProps,
} from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import cx from 'classnames';
import { GetServerSideProps } from 'next';
import { Provider } from 'react-redux';

import { ColorFamily, MarketingProductCard } from '~/components/common/cards/MarketingProductCard';
import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import type { VendorCardVendor } from '~/components/common/cards/util/types';
import VendorHead from '~/components/common/layouts/head/VendorHead';
import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import ModalRoot from '~/components/common/ModalRoot';
import Notifications from '~/components/common/notifications/Notifications';
import ToastsContainer from '~/components/common/notifications/ToastsContainer';
import VendorsByCategory from '~/components/VendorsByCategory/VendorsByCategory';
import { SearchedLocationProvider, useSearchedLocation } from '~/contexts/SearchContext';
import { UserContextProvider, useUserContext } from '~/contexts/UserContext';
import { SessionStorageToasts } from '~/hooks/useToastsFromLocalStorage';
import useWeddingLocation from '~/hooks/useWeddingLocation';
import { fetchStorefrontSearchResults } from '~/libs/client/api/storefronts';
import { getNavHtml } from '~/libs/server/api/nav';
import { fetchStorefrontSearchResults as serverFetchStorefrontSearchResults } from '~/libs/server/api/storefronts';
import { getUserContext } from '~/libs/server/api/user';
import { getExpertAdviceRelatedLinks, getSeoContentBlockHtml } from '~/libs/universal/seo';
import HERO_BACKGROUND from '~/pages/couples/explore/assets/hero-background.jpg';
import PROMO_REAL_WEDDINGS from '~/pages/couples/explore/assets/promo-real-weddings.jpg';
import PROMO_SAMPLES from '~/pages/couples/explore/assets/promo-samples.jpg';
import ByStyleCategoryCarousel from '~/pages/couples/explore/components/ByStyleCategoryCarousel/ByStyleCategoryCarousel';
import { Divider } from '~/pages/couples/explore/components/CategoryLandingPage/Divider/Divider';
import SeoLinkListSection, {
  SeoLinkListItem,
} from '~/pages/couples/explore/components/CategoryLandingPage/SeoLinkListSection/SeoLinkListSection';
import { StatesSection } from '~/pages/couples/explore/components/CategoryLandingPage/StatesSection/StatesSection';
import ExpertAdvice from '~/pages/couples/explore/components/ExpertAdvice/ExpertAdvice';
import LandingDifferentiation from '~/pages/couples/explore/components/LandingDifferentiation/LandingDifferentiation';
import MostPopularShelf from '~/pages/couples/explore/components/MostPopularShelf/MostPopularShelf';
import { OverlaySearchBar } from '~/pages/couples/explore/components/OverlaySearchBar/OverlaySearchBar';
import { PageSection } from '~/pages/couples/explore/components/PageSection/PageSection';
import PopularLocations from '~/pages/couples/explore/components/PopularLocations/PopularLocations';
import EditLocationModal from '~/pages/couples/explore/components/SearchBar/EditLocationModal';
import type {
  VendorSearchRequest,
  VendorSearchRequestType,
} from '~/pages/couples/explore/components/SearchResults/types/types';
import PersonalizedLandingPage from '~/pages/couples/explore/PersonalizedLandingPage';
import { defaultSearchLocation, getBriefLocation } from '~/pages/couples/explore/util/helpers';
import { getStore } from '~/store';
import type { LocationOrAllMarketsType } from '~/types/responseTypes';
import type { FaqContent } from '~/types/types';
import type { IUserContext } from '~/types/userContext';
import ApiService from '~/util/apiService';
import { isAutomatedTest, isAutomatedTestSSR } from '~/util/automatedTestUtils';
import EXPERIMENT_FLAGS, { EXPERIMENT_FLAGS_SSR, MARKETPLACE_CLP_LP } from '~/util/experimentFlags';
import { getAssetImageUrl } from '~/util/imageUtils';
import { SEARCHED_LOCATION_KEY, getSearchLocationForBriefLocation } from '~/util/searchUtils';
import { getFaqSchema, mapGraphiteRelatedLinks, renderSchemas } from '~/util/seoUtils';
import { TrackingProductLocation } from '~/util/trackingHelper';
import { hasMarketplaceAccount } from '~/util/userContextUtils';
import vendorSearchUrl from '~/util/vendorSearchUrl';

import styles from './index.module.less';
interface LandingPageProps {
  navHtml: string | null;
  userContext: IUserContext | null;
  relatedLinks: RelatedLinkView[];
  searchedLocation: LocationOrAllMarketsType | undefined;
  adjacentCities?: string[];
  mostPopularVenues?: VendorCardVendor[];
  userAgent?: string;
  isClpTest: boolean;
}

const FAQS: FaqContent[] = [
  {
    question: 'How does Zola decide which wedding vendors can join its community?',
    answer:
      'We require every vendor to say Yes to our Vendor Vows. That means they pledge to support every couple\u2019s right to marry and treat every couple equally while vowing to communicate honestly and provide the best possible experience throughout the wedding planning process.',
  },
  {
    question: 'What\u2019s the easiest way to get started?',
    answer:
      'There\u2019s no right or wrong way to plan, so dive in wherever you\u2019d like. Many of our couples build their free wedding website first, while some get a head start on their registry. We make it easy no matter where you begin your journey. For more guidance, visit our Expert Advice section.',
  },
  {
    question: 'Do we need a wedding date before we start planning?',
    answer:
      'Nope! You can take a crack at your wedding website, your registry, or even your guest list. Once you have a date, let us know and we\u2019ll make sure you\u2019re running on schedule. Not sure how to set your wedding date? Visit this Expert Advice post.',
  },
  {
    question: 'How is Zola free to use?',
    answer:
      '“Free” and “wedding” rarely appear in the same sentence, so we get this question a lot. We\u2019re a store and like any online retailer, we make money on the products we sell. So we offer couples free tools to help streamline their wedding planning.',
  },
  {
    question: 'If we\u2019re planning a simple wedding, do we still need Zola?',
    answer:
      'We may be a touch biased, but… definitely! Whether you\u2019re saying “I do” in front of 20 people or 400, hosting an intimate reception or an epic dance party, there are still logistics to plan and we want to help with every single one of them.',
  },
  {
    question: 'What if we\u2019re registering at other stores AND Zola?',
    answer:
      'That\u2019s totally fine! We filled our store with 50,000 of the best gifts, but for those special things we don\u2019t have, you can add them to your registry from any online store. That way, guests can shop all your gifts in one place.',
  },
];

const faqSchemas = [getFaqSchema(FAQS)];

const PreAuthContent = ({ isClpTest }: Pick<LandingPageProps, 'isClpTest'>) => {
  const userContext = useUserContext();
  const { isMobile } = useResponsiveDesign();
  const [showLocationModal, setShowLocationModal] = useState(false);

  const mastheadTitle = (
    <h1>
      Wedding <span>venues and vendors</span>: Let&rsquo;s find your (other) perfect match
    </h1>
  );

  const mastheadImages: FeaturedImageProps = useMemo(() => {
    return {
      background: {
        type: BackgroundType.IMAGE,
        parameters: {
          featuredSrc: isMobile
            ? getAssetImageUrl(HERO_BACKGROUND, { width: 700, quality: 80 })
            : getAssetImageUrl(HERO_BACKGROUND, { width: 1000, quality: 80 }),
          alt: '',
        },
      },
    };
  }, [isMobile]);

  if (hasMarketplaceAccount(userContext)) {
    return null;
  }

  return (
    <>
      <section>
        <Masthead
          productName="Wedding venues and vendors"
          title={mastheadTitle}
          valueProps={[
            'Discover, inquire with, and book vendors who are right for you',
            'Transparent pricing and vendors for every budget',
            'Plus, personalized recs to match your style',
          ]}
          btnText="Take our quiz"
          btnHref="/inspiration/onboard"
          images={mastheadImages}
          isPhotoOnRight
          className={styles.mastHead}
          hideImageNonDesktop
        />
      </section>
      <section className={cx(styles.withBorder, { [styles.hideNotDesktop]: isClpTest })}>
        <OverlaySearchBar isClpTest={isClpTest} />
      </section>
      <section
        className={cx(styles.withBorder, styles.vendorsByCategory, {
          [styles.isClpTest]: isClpTest,
        })}
      >
        <VendorsByCategory isClpTest={isClpTest} showModal={() => setShowLocationModal(true)} />
        {showLocationModal && <EditLocationModal hideModalFn={() => setShowLocationModal(false)} />}
      </section>
    </>
  );
};

const LandingPageProviders = (props: LandingPageProps) => {
  const { userContext, userAgent, searchedLocation } = props;
  const { weddingLocation } = useWeddingLocation();
  const briefLocation = getBriefLocation(weddingLocation);
  const canonicalLocation = searchedLocation || getSearchLocationForBriefLocation(briefLocation);
  return (
    <UserContextProvider userContext={userContext}>
      <ResponsiveDesignProvider userAgent={userAgent}>
        <SearchedLocationProvider searchedLocation={canonicalLocation}>
          <Provider store={getStore()}>
            <LandingPage {...props} />
          </Provider>
        </SearchedLocationProvider>
      </ResponsiveDesignProvider>
    </UserContextProvider>
  );
};

const LandingPage = ({
  navHtml,
  userContext,
  relatedLinks,
  adjacentCities: serverAdjacentCities,
  mostPopularVenues: serverMostPopularVenues,
  isClpTest,
}: LandingPageProps) => {
  const { searchedLocation } = useSearchedLocation();
  const [adjacentCities, setAdjacentCities] = useState<string[] | undefined>(serverAdjacentCities);
  const [mostPopularVenues, setMostPopularVenues] = useState<VendorCardVendor[] | undefined>(
    serverMostPopularVenues
  );
  const [hasPersonalizedContentLoaded, setHasPersonalizedContentLoaded] = useState(
    !userContext?.has_marketplace_account
  );

  useEffect(() => {
    if (isClpTest) {
      const vendorSearch: VendorSearchRequest = {
        page: 1,
        selectedLocation: searchedLocation as LocationOrAllMarketsType,
        selectedTaxonomy: {
          id: 1,
          key: VENUES_TAXONOMY_KEY,
          label: 'venues',
        },
      };
      const searchRequest: VendorSearchRequestType = {
        offset: 0,
        limit: 10,
        vendorType: getVendorType(VENUES_TAXONOMY_KEY) as V4StorefrontSearchRequest.VendorTypeEnum,
        locationMethod: 'MARKET',
        pointRadius: null,
        metroTypes: null,
        price: null,
        capacity: null,
        facetGroups: null,
        targetDates: null,
        sortBy: 'REVIEW_SCORE_SIZE',
        sortOrder: 'DESC',
        allowUnclaimedListings: false,
        searchName: 'DEFAULT',
        boostFeaturedStorefronts: null,
        awardGroups: null,
        city: searchedLocation?.storefrontSearchPayload?.city || null,
        stateProvince: searchedLocation?.storefrontSearchPayload?.stateProvince || null,
        vendorMarketId: searchedLocation?.storefrontSearchPayload?.vendorMarketId || null,
      };

      Promise.all([
        getSeoContentBlockHtml(vendorSearch, ApiService),
        fetchStorefrontSearchResults(searchRequest),
      ])
        .then(([seoContent, _mostPopularVenues]) => {
          setAdjacentCities(
            (seoContent as SeoContentView)?.faqs?.filter(
              (block) => block.contentKey === 'ADJACENT_CITIES'
            )[0].htmlContent || []
          );
          setMostPopularVenues(
            _mostPopularVenues?.entities.map((v) => cardViewToVendorCardVendor(v))
          );
        })
        .catch(null);
    }
  }, [searchedLocation, isClpTest]);

  useEffectOnce(() => {
    // Trigger experiment viewed
    !userContext?.is_guest &&
      !isAutomatedTest() &&
      EXPERIMENT_FLAGS.isUnderTest(MARKETPLACE_CLP_LP, userContext, true);
  });

  return (
    <MainLayout
      activeSecondaryLink={SecondaryNavIdentifier.EXPLORE_VENDORS}
      ssrNavHtml={navHtml}
      isClpTest={isClpTest}
    >
      <VendorHead canonicalUrl="https://www.zola.com/wedding-vendors" imageUrl={HERO_BACKGROUND} />
      <div className={styles.landingPage}>
        <PreAuthContent isClpTest={isClpTest} />
        {!hasPersonalizedContentLoaded && <div className={styles.personalizedContentPlaceholder} />}
        <SessionStorageToasts />
        <PersonalizedLandingPage
          setHasPersonalizedContentLoaded={setHasPersonalizedContentLoaded}
          isClpTest={isClpTest}
        />
        <section>
          {isClpTest ? (
            <>
              <ByStyleCategoryCarousel />
              <MostPopularShelf
                cardList={mostPopularVenues || []}
                viewAllHref={vendorSearchUrl(VENUES_TAXONOMY_KEY, searchedLocation?.slug)}
              />
              <Divider />
            </>
          ) : (
            <LandingDifferentiation />
          )}
        </section>
        <section>
          <PopularLocations isClpTest={isClpTest} />
        </section>
        {isClpTest ? (
          <>
            <PageSection>
              <ExpertAdvice
                title="Get the inside scoop on wedding & reception venues"
                articleSlugs={[
                  'when-should-i-book-my-wedding-venue',
                  'questions-to-ask-when-touring-a-wedding-venue',
                  'how-to-find-an-affordable-wedding-venue',
                  'which-type-of-wedding-venue-is-right-for-you',
                  'what-to-know-look-for-in-your-wedding-venue-contract',
                  'how-to-choose-a-wedding-venue',
                  'all-inclusive-wedding-venues-pros-and-cons',
                ]}
                showReadMore={false}
                noRecomputeToShowAll
              />
            </PageSection>
            {Boolean(adjacentCities?.length) && <Divider />}
            <SeoLinkListSection
              title="Wedding and reception venues in nearby cities"
              titleAlignment="center"
            >
              {adjacentCities?.map((link, i) => (
                <SeoLinkListItem
                  key={`adjacent-city-${i}`}
                  dangerouslySetInnerHTML={{ __html: link }}
                />
              ))}
            </SeoLinkListSection>
            <Divider />
            <StatesSection
              vendorType={VENUES_TAXONOMY_KEY}
              title="Wedding and reception venues by state"
              titleAlignment="center"
            />
            <Divider />
          </>
        ) : (
          <section className={styles.withBorder}>
            <div className={styles.marketingProductCards}>
              <div className={styles.marketingProductCardsWrapper}>
                <MarketingProductCard
                  image={PROMO_REAL_WEDDINGS}
                  imageWidth={384}
                  altText=""
                  header="Get inspired"
                  bodyText="Explore photos of real, recent weddings and find the vendors behind your faves."
                  buttonText="Scroll real weddings"
                  url="/inspiration"
                  colorFamily={ColorFamily.BlueRefresh}
                  location={TrackingProductLocation.EXPLORE_VENDORS_PLP}
                  position={10}
                  businessUnit={BUSINESS_UNIT.MARKETPLACE}
                  section="LANDING_PAGE"
                />
                <div className={styles.divider} />
                <MarketingProductCard
                  image={PROMO_SAMPLES}
                  imageWidth={384}
                  altText=""
                  header="Premium quality, perfectly priced"
                  bodyText="Highly customizable designs for every wedding moment."
                  buttonText="Choose free samples"
                  url="/wedding-planning/save-the-date/shop?multi_sample_mode=multisampleenabled"
                  colorFamily={ColorFamily.Forest}
                  location={TrackingProductLocation.EXPLORE_VENDORS_PLP}
                  position={11}
                  businessUnit={BUSINESS_UNIT.MARKETPLACE}
                  section="LANDING_PAGE"
                />
              </div>
            </div>
          </section>
        )}
        <section>
          {renderSchemas(faqSchemas)}
          <SupportAccordion faqs={FAQS} advice={{ links: mapGraphiteRelatedLinks(relatedLinks) }} />
        </section>
      </div>
      <ModalRoot />
      <Notifications />
      <ToastsContainer />
    </MainLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const userAgent = context.req.headers['user-agent'];

  const [navHtml, userContext, relatedLinks] = await Promise.all([
    getNavHtml(context.req),
    getUserContext(context.req),
    getExpertAdviceRelatedLinks('venues-vendors', 'wedding-venues'),
  ]);

  const isClpTest =
    userContext?.is_guest || // this experiment won for all guest users, so now we're testing post-auth
    (!isAutomatedTestSSR(context.req.cookies || {}) &&
      !userContext?.is_guest &&
      EXPERIMENT_FLAGS_SSR.getExperimentVariation(
        MARKETPLACE_CLP_LP,
        userContext,
        context.req.cookies || {}
      ) === 'TEST-1');
  const searchedLocationCookie = context.req.cookies?.[SEARCHED_LOCATION_KEY];
  const searchedLocation = searchedLocationCookie
    ? (JSON.parse(searchedLocationCookie) as LocationOrAllMarketsType)
    : null;

  const conditionalSearchParams: {
    city: string | null;
    stateProvince: string | null;
    vendorMarketId: number | null;
  } = searchedLocation
    ? {
        city: searchedLocation?.storefrontSearchPayload?.city || null,
        stateProvince: searchedLocation?.storefrontSearchPayload?.stateProvince || null,
        vendorMarketId: searchedLocation?.storefrontSearchPayload?.vendorMarketId || null,
      }
    : {
        city: defaultSearchLocation.name,
        stateProvince: defaultSearchLocation.stateCode,
        vendorMarketId: defaultSearchLocation.vendorMarket.id,
      };

  const searchRequest: VendorSearchRequestType = {
    offset: 0,
    limit: 10,
    vendorType: getVendorType(VENUES_TAXONOMY_KEY) as V4StorefrontSearchRequest.VendorTypeEnum,
    locationMethod: 'MARKET',
    pointRadius: null,
    metroTypes: null,
    price: null,
    capacity: null,
    facetGroups: null,
    targetDates: null,
    sortBy: 'REVIEW_SCORE_SIZE',
    sortOrder: 'DESC',
    allowUnclaimedListings: false,
    searchName: 'DEFAULT',
    boostFeaturedStorefronts: null,
    awardGroups: null,
    ...conditionalSearchParams,
  };

  const vendorSearch: VendorSearchRequest = {
    page: 1,
    selectedLocation: searchedLocation,
    selectedTaxonomy: isClpTest
      ? {
          id: 1,
          key: VENUES_TAXONOMY_KEY,
          label: 'venues',
        }
      : null,
  };

  const [seoContent, mostPopularVenues] = isClpTest
    ? await Promise.all([
        getSeoContentBlockHtml(vendorSearch),
        serverFetchStorefrontSearchResults(searchRequest, context.req),
      ])
    : [];

  const adjacentCities =
    (seoContent as SeoContentView)?.faqs?.filter(
      (block) => block.contentKey === 'ADJACENT_CITIES'
    )[0].htmlContent || [];

  return {
    props: {
      navHtml,
      userContext,
      userAgent,
      relatedLinks,
      isClpTest,
      searchedLocation,
      adjacentCities,
      mostPopularVenues:
        mostPopularVenues?.entities.map((v) => cardViewToVendorCardVendor(v)) || null,
    },
  };
};

export default LandingPageProviders;
